import { MouseEvent } from 'react'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'

import styled from 'styled-components'
import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  Divider,
  Grid,
  P,
  Wrapper,
} from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'

import { getUserIdFromToken } from 'lib/authentication/token'
import useApiHelpers from 'lib/effects/api-helpers'
import { TaskStage } from 'lib/models/task-stages'
import OwnerAssignmentDropdown from 'components/owner-assignment-dropdown'
import { useTaskStagesContext } from 'components/tasks-panel/context'

const StyledStageCard = styled(Grid.Item)<{ $isSelected: boolean }>`
  background: ${COLOR.BACKGROUND.FOG};
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected ? COLOR.STATE.SUCCESS : COLOR.GREY.LIGHT};
`

const StyledStageButton = styled(Button.Plain)<{ $isSelected: boolean }>`
  // prevents the active state blue outline when clicked
  &:not([disabled]):active {
    box-shadow: none;
  }
  color: ${COLOR.BLACK};
  padding: 0 0 ${GTR.S} 0;
  display: flex;
  justify-content: space-between;
`

const StyledFlexWrapper = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
`

const StyledAssignOwnerButton = styled(Button.Underline)`
  padding: 0;
  font-size: ${FONT.SIZE.S};
  color: ${COLOR.BLACK};
`

export const TaskStageFilterPanel = ({
  probateCaseId,
  selectedTaskStageId,
  setSelectedTaskStageId,
}: {
  probateCaseId: number
  taskStages: TaskStage[]
  selectedTaskStageId: number
  setSelectedTaskStageId: (id: number) => void
}) => {
  const { taskStages, fetchTaskStages } = useTaskStagesContext()
  const token = useSelector((state: { token: string }) => state.token)
  const loggedInAdminUserId = getUserIdFromToken(token)

  const { createItem: assignOwner, isCreating: isAssigningOwner } =
    useApiHelpers({
      baseUrl: `/api/probate-cases/${probateCaseId}/task-stages/${selectedTaskStageId}/add-owner`,
      type: 'task_stages',
    })

  const handleAssignStageOwner = async (ownerId: number | null) => {
    if (ownerId == null) return

    await assignOwner({
      ownerId,
    })

    fetchTaskStages()
    toast(`Assigned task stage owner.`, {
      toastId: 'task-stage-owner-assigned',
    })
  }

  return (
    <Grid gap={0}>
      {taskStages.map((stage) => {
        const isSelected = stage.id === selectedTaskStageId
        const {
          attributes: {
            label,
            slaInDays,
            ownerId,
            owner,
            completedCoreTasksCount,
            totalCoreTasksCount,
            status,
            daysInStage,
          },
        } = stage

        return (
          <StyledStageCard
            key={stage.id}
            $isSelected={isSelected}
            borderRadius="M"
            padding={['M', 'S']}
            margin={['S', 0]}
          >
            <StyledStageButton
              $isSelected={isSelected}
              aria-current={isSelected ? 'true' : undefined}
              aria-expanded={isSelected}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.currentTarget.blur() // prevent the active state blue outline when clicked
                setSelectedTaskStageId(stage.id)
              }}
              icon={
                isSelected ? (
                  <ChevronDownIcon size="S" />
                ) : (
                  <ChevronUpIcon size="S" />
                )
              }
              stretch
            >
              {label}
            </StyledStageButton>
            <P
              size="S"
              strong={status === 'Completed' ? true : false}
              color={status === 'Completed' ? COLOR.STATE.SUCCESS : COLOR.BLACK}
            >
              {status} • {completedCoreTasksCount} of {totalCoreTasksCount} core
              tasks
            </P>

            {isSelected && (
              <>
                <Divider margin={['S', 0]} />

                <StyledFlexWrapper>
                  <P color={COLOR.BLACK} margin={[0, 0, 'XS', 0]} size="S">
                    SLA:{' '}
                  </P>
                  <P color={COLOR.BLACK} strong size="S">
                    {slaInDays} days
                  </P>
                </StyledFlexWrapper>
                <StyledFlexWrapper>
                  <P color={COLOR.BLACK} margin={0} size="S">
                    {status === 'Completed'
                      ? `Completed in:`
                      : `Days in stage:`}
                  </P>
                  <P color={COLOR.BLACK} strong size="S">
                    {daysInStage || status !== 'Not started'
                      ? `${daysInStage} day${daysInStage === 1 ? '' : 's'}`
                      : '-'}
                  </P>
                </StyledFlexWrapper>
                <Divider margin={['S', 0]} />
                <StyledFlexWrapper>
                  <P color={COLOR.BLACK} margin={0} size="S">
                    Stage owner:{' '}
                  </P>
                  <OwnerAssignmentDropdown
                    loggedInAdminUserId={loggedInAdminUserId}
                    initialAdminUserId={ownerId}
                    isSubmitting={isAssigningOwner}
                    onAssign={handleAssignStageOwner}
                    trigger={
                      <StyledAssignOwnerButton>
                        {owner ? owner.name : 'Unassigned'}
                      </StyledAssignOwnerButton>
                    }
                  />
                </StyledFlexWrapper>
              </>
            )}
          </StyledStageCard>
        )
      })}
    </Grid>
  )
}
